'use client';

import Script from 'next/script';
import { type ComponentProps, useEffect, useState } from 'react';

type Props = Omit<ComponentProps<typeof Script>, 'strategy'> & {
  /** Id of the element the script renders into. */
  targetId: string;
};

/**
 * Loads an external widget script once the element it renders into comes
 * within a viewport of the screen. Renders nothing until then, and waits for
 * the element if it is not in the page yet.
 */
export function ScriptOnVisible({ targetId, ...scriptProps }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const intersection = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        intersection.disconnect();
        setVisible(true);
      },
      { rootMargin: '100% 0px' }
    );
    const target = document.getElementById(targetId);
    if (target != null) {
      intersection.observe(target);
      return () => intersection.disconnect();
    }
    const mutation = new MutationObserver(() => {
      const added = document.getElementById(targetId);
      if (added == null) return;
      mutation.disconnect();
      intersection.observe(added);
    });
    mutation.observe(document.body, { childList: true, subtree: true });
    return () => {
      mutation.disconnect();
      intersection.disconnect();
    };
  }, [targetId, visible]);

  return visible ? (
    <Script strategy="afterInteractive" {...scriptProps} />
  ) : null;
}
